import { format, isValid } from 'date-fns';
import { toZonedTime } from 'date-fns-tz/toZonedTime';
import { getLanguage } from './i18n';

const TIMEZONE_BRASILIA = 'America/Sao_Paulo';

function parseToDate(isoOrDate) {
  if (!isoOrDate) return null;
  if (isoOrDate instanceof Date) return isoOrDate;
  const str = String(isoOrDate).trim();
  if (!str) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return new Date(str + 'T12:00:00.000Z');
  }
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(str) && !str.includes('Z') && !str.includes('+') && !str.includes('-', 10)) {
    return new Date(str.replace(' ', 'T') + 'Z');
  }
  return new Date(str);
}

function toBrasilia(date) {
  if (!date || !isValid(date)) return null;
  return toZonedTime(date, TIMEZONE_BRASILIA);
}

export function formatDateTime(isoOrDate) {
  const d = parseToDate(isoOrDate);
  if (!d || !isValid(d)) return '';
  const brasilia = toBrasilia(d);
  const lang = getLanguage();
  if (lang === 'en-US') {
    return format(brasilia, 'MM/dd/yyyy hh:mm:ss a');
  }
  return format(brasilia, 'dd/MM/yyyy HH:mm:ss');
}

export function formatDate(isoOrDate) {
  const d = parseToDate(isoOrDate);
  if (!d || !isValid(d)) return '';
  const brasilia = toBrasilia(d);
  const lang = getLanguage();
  if (lang === 'en-US') {
    return format(brasilia, 'MM/dd/yyyy');
  }
  return format(brasilia, 'dd/MM/yyyy');
}

export function parseDateBR(str) {
  if (!str || typeof str !== 'string') return null;
  const cleaned = str.replace(/\D/g, '');
  if (cleaned.length !== 8) return null;
  const day = parseInt(cleaned.slice(0, 2), 10);
  const month = parseInt(cleaned.slice(2, 4), 10) - 1;
  const year = parseInt(cleaned.slice(4, 8), 10);
  const d = new Date(year, month, day);
  return isValid(d) ? d : null;
}

export function formatDateToISO(date) {
  if (!date || !(date instanceof Date) || !isValid(date)) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function applyDateMask(value) {
  if (value === null || value === undefined) return '';
  const cleaned = String(value).replace(/\D/g, '');
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
  return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
}

export function handleDateInputChange(event, onChange) {
  const inputValue = event.target.value;
  const cleaned = inputValue.replace(/\D/g, '');
  if (cleaned.length === 0) {
    onChange('');
    return;
  }
  const limited = cleaned.slice(0, 8);
  let formatted = '';
  if (limited.length <= 2) {
    formatted = limited;
  } else if (limited.length <= 4) {
    formatted = `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else {
    formatted = `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  }
  const syntheticEvent = {
    ...event,
    target: {
      ...event.target,
      value: formatted,
    },
  };
  onChange(syntheticEvent);
}

export function isoToDisplayBR(isoDateStr) {
  if (!isoDateStr || typeof isoDateStr !== 'string') return '';
  const ymd = isoDateStr.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

export function displayBRToISO(displayStr) {
  const parsed = parseDateBR(displayStr);
  return parsed ? formatDateToISO(parsed) : '';
}

export function apiDateTimeToDatetimeLocal(apiStr) {
  if (!apiStr || typeof apiStr !== 'string') return '';
  const s = apiStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s + 'T00:00';
  const normalized = s.replace(' ', 'T').slice(0, 16);
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized)) return '';
  return normalized;
}

export function datetimeLocalToApi(htmlValue) {
  if (!htmlValue || typeof htmlValue !== 'string') return '';
  const normalized = htmlValue.trim().replace('T', ' ').slice(0, 16);
  if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(normalized)) return '';
  return normalized + ':00';
}

export function apiDateTimeToDisplay(apiStr) {
  const d = parseToDate(apiStr);
  if (!d || !isValid(d)) return '';
  const brasilia = toBrasilia(d);
  const lang = getLanguage();
  if (lang === 'en-US') {
    return format(brasilia, 'MM/dd/yyyy HH:mm');
  }
  return format(brasilia, 'dd/MM/yyyy HH:mm');
}

export function displayDateTimeToApi(displayStr) {
  if (!displayStr || typeof displayStr !== 'string') return '';
  const s = displayStr.trim();
  const lang = getLanguage();
  if (lang === 'en-US') {
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/);
    if (!m) return '';
    const month = parseInt(m[1], 10);
    const day = parseInt(m[2], 10);
    const year = parseInt(m[3], 10);
    const hour = parseInt(m[4], 10);
    const min = parseInt(m[5], 10);
    const d = new Date(year, month - 1, day, hour, min);
    return isValid(d) ? format(d, 'yyyy-MM-dd HH:mm:ss') : '';
  }
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/);
  if (!m) return '';
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  const hour = parseInt(m[4], 10);
  const min = parseInt(m[5], 10);
  const d = new Date(year, month - 1, day, hour, min);
  return isValid(d) ? format(d, 'yyyy-MM-dd HH:mm:ss') : '';
}

export function getDateTimeInputPlaceholder() {
  const lang = getLanguage();
  if (lang === 'en-US') {
    return 'MM/DD/YYYY HH:mm';
  }
  return 'DD/MM/AAAA HH:mm';
}

export function handleDateTimeInputChange(event, onChange) {
  const inputValue = event.target.value;
  const cleaned = inputValue.replace(/\D/g, '');
  const lang = getLanguage();
  if (cleaned.length === 0) {
    onChange('');
    return;
  }
  const limited = cleaned.slice(0, 12);
  let formatted = '';
  if (limited.length <= 2) {
    formatted = limited;
  } else if (limited.length <= 4) {
    formatted = `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else if (limited.length <= 8) {
    formatted = `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  } else {
    formatted = `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4, 8)} ${limited.slice(8, 10)}:${limited.slice(10, 12)}`;
  }
  const syntheticEvent = {
    ...event,
    target: {
      ...event.target,
      value: formatted,
    },
  };
  onChange(syntheticEvent);
}
